/**
 * RecipeShape (S2) — the Shape: a miniature article skeleton rendered
 * deterministically from the recipe's clamped values + floor booleans.
 *
 * Presentation seam: props-only (ephemeral UI state only; no data/app state).
 * All ordering/presence decisions come from buildShapeModel — this component
 * is a thin renderer. Color is semantic and token-only:
 *   coral = prose the writer generates (intro/section bars),
 *   teal  = structured components (TOC/takeaways/list/image/callout/table/FAQ),
 *   ink   = fixed chrome (title/CTA).
 * Ghost section bars render the min–max range (§5.1).
 *
 * NEVER draws: byline, SEO-METADATA (export-only artifacts).
 * Compact recipes render deliberately-complete skeletons of their class —
 * absent-by-design elements do not exist in the model (no holes, no absence
 * markers; absence signaling is the S4 detail pane's fixed-slot row).
 *
 * No visual evidence is produced or asserted here — structural DOM only
 * (Carl standing directive; visual pass deferred).
 */

import React from 'react';
import { GenerationRecipe } from '../recipeTypes';
import { ShapeFloorPayload } from './shapeFloor';
import { ShapeElement, buildShapeModel } from './buildShapeModel';
import { cn } from '@/lib/utils';

export interface RecipeShapeProps {
  /** The recipe (output of defaultConfigToRecipe — the ONLY deserializer). */
  recipe: GenerationRecipe;
  /** Parsed v1.2 shape floor (parseShapeFloor output), or undefined. */
  floor?: ShapeFloorPayload;
  /** full = detail/edit surfaces; strip = card thumbnail (same model). */
  variant?: 'full' | 'strip';
  className?: string;
}

const ElementMark: React.FC<{ element: ShapeElement; variant: 'full' | 'strip' }> = ({
  element,
  variant,
}) => {
  const compact = variant === 'strip';
  switch (element.kind) {
    case 'title':
      return (
        <div
          data-shape-element="title"
          className={cn('rounded-sm bg-ink', compact ? 'h-1.5 w-3/4' : 'h-2.5 w-3/4')}
        />
      );
    case 'toc':
      return (
        <div
          data-shape-element="toc"
          className={cn(
            'rounded-sm border border-teal-dark bg-teal-light',
            compact ? 'h-2 w-1/2' : 'h-4 w-1/2',
          )}
        />
      );
    case 'key-takeaways':
      return (
        <div
          data-shape-element="key-takeaways"
          className={cn('rounded-sm bg-teal', compact ? 'h-1.5 w-2/3' : 'h-2 w-2/3')}
        />
      );
    case 'intro':
      return (
        <div
          data-shape-element="intro"
          className={cn('rounded-sm bg-coral-light', compact ? 'h-2 w-full' : 'h-3 w-full')}
        />
      );
    case 'section':
      return (
        <div
          data-shape-element="section"
          data-section-index={element.index}
          data-ghost={element.ghost ? 'true' : 'false'}
          data-words={element.words}
          className={cn(
            'flex items-center gap-1 rounded-sm',
            element.ghost
              ? 'border border-dashed border-coral-dark bg-coral-light'
              : 'bg-coral',
            compact ? 'h-1.5 w-full' : 'h-2.5 w-full',
          )}
        >
          {element.h3 && (
            <span
              data-shape-h3="true"
              className={cn('inline-block rounded-sm bg-coral-dark', compact ? 'h-0.5 w-2' : 'h-1 w-3')}
            />
          )}
          {!compact && (
            <span className="sr-only">
              {element.ghost ? 'possible section' : 'section'} {element.index}, ~{element.words} words
            </span>
          )}
        </div>
      );
    case 'list':
      return (
        <div
          data-shape-element="list"
          className={cn('rounded-sm bg-teal', compact ? 'h-1 w-1/3' : 'h-1.5 w-1/3')}
        />
      );
    case 'image':
      return (
        <div
          data-shape-element="image"
          data-image-index={element.index}
          className={cn(
            'rounded-sm border border-teal-dark bg-teal-light',
            compact ? 'h-2 w-1/4' : 'h-4 w-1/4',
          )}
        />
      );
    case 'callout':
      return (
        <div
          data-shape-element="callout"
          data-callout-index={element.index}
          className={cn('rounded-sm bg-teal-light border border-teal', compact ? 'h-1.5 w-2/5' : 'h-2 w-2/5')}
        />
      );
    case 'table':
      return (
        <div
          data-shape-element="table"
          className={cn('rounded-sm border border-teal-dark bg-teal-light', compact ? 'h-2 w-3/5' : 'h-3.5 w-3/5')}
        />
      );
    case 'cta':
      return (
        <div
          data-shape-element="cta"
          className={cn('rounded-sm bg-ink-2', compact ? 'h-1.5 w-1/2' : 'h-2 w-1/2')}
        />
      );
    case 'faq':
      return (
        <div
          data-shape-element="faq"
          data-faq-count={element.count}
          className={cn('rounded-sm bg-teal', compact ? 'h-1.5 w-2/3' : 'h-2 w-2/3')}
        >
          {!compact && <span className="sr-only">FAQ, {element.count} questions</span>}
        </div>
      );
    default:
      return null;
  }
};

export const RecipeShape: React.FC<RecipeShapeProps> = ({
  recipe,
  floor,
  variant = 'full',
  className,
}) => {
  const model = buildShapeModel(recipe, floor);
  return (
    <div
      data-testid="recipe-shape"
      data-variant={variant}
      role="img"
      aria-label="Article shape preview"
      className={cn('flex flex-col', variant === 'strip' ? 'gap-0.5' : 'gap-1', className)}
    >
      {model.elements.map((element, i) => (
        <ElementMark key={`${element.kind}-${i}`} element={element} variant={variant} />
      ))}
    </div>
  );
};
